import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { sortBy, sortedUniqBy } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

import { formatDateTime } from 'helpers/formatters/date_time_formats';
import { getTaskTypeLabel } from 'helpers/tasks/helpers';

import SearchPage from 'components/search_page';
import SelectFilter from 'components/search_sidebar/select_filter';
import FilterTopbar from 'components/search_page/filter_topbar';
import FilterRow from 'components/search_page/filter_row';
import SelectableRecordTable from 'components/selectable_record_table/selectable_record_table';
import SelectableRow from 'components/selectable_record_table/selectable_row';

export default class LeadsTable extends React.Component {
  static propTypes = {
    editable: PropTypes.bool,
    hideLastActivity: PropTypes.bool,
    leads: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      createdAt: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      lastActivityDate: PropTypes.string,
      lastActivityType: PropTypes.string,
      leadData: PropTypes.shape({
        name: PropTypes.string
      }).isRequired,
      listingId: PropTypes.number,
      listingLabel: PropTypes.string,
      noteToClient: PropTypes.shape({
        body: PropTypes.string
      }).isRequired,
      referralSourceLabel: PropTypes.string,
      status: PropTypes.number,
      statusLabel: PropTypes.string
    })),
    leadStatuses: PropTypes.array.isRequired,
    savedNoteForLeadId: PropTypes.number,
    onUpdateNote: PropTypes.func
  };

  state = {
    searchText: '',
    statusFilter: null,
    listingFilter: null
  }

  showListingData = () => this.listings().length > 1

  listings = () => {
    const { leads } = this.props;
    let listings = leads.filter(l => l.listingId).map(l => ({ id: l.listingId, label: l.listingLabel }));
    listings = sortedUniqBy(sortBy(listings, 'id'), 'id');

    return listings.length > 1 ? [{ id: '', label: 'All Spaces' }, ...listings] : listings;
  }

  filteredLeads = () => {
    const { listingFilter, statusFilter, searchText } = this.state;

    let leads = this.props.leads;

    if (listingFilter) leads = leads.filter(lead => lead.listingId === listingFilter);
    if (statusFilter) leads = leads.filter(lead => lead.status === statusFilter);
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      leads = leads.filter(lead => lead.leadData.name.toLowerCase().includes(lowerSearchText));
    }

    return leads;
  }

  changeListingFilter = (listing) =>  {
    this.setState({ listingFilter: listing.id });
  }

  changeSearchText = (e) => {
    this.setState({ searchText: e.target.value || '' });
  }

  changeStatusFilter = (status) =>  {
    this.setState({ statusFilter: status.id });
  }

  currentListing = () => this.listings().find(listing => listing.id === this.state.listingFilter)

  currentStatus = () => this.props.leadStatuses.find(leadStatus => leadStatus.id === this.state.statusFilter)

  leadStatuses = () => [{ id: '', label: 'All Statuses' }, ...this.props.leadStatuses]

  hasNotes = lead => !!lead.noteToClient.body

  showNotesColumn = () => this.props.editable || this.props.leads.some(this.hasNotes)

  updateNote = (leadId, e) => {
    this.props.onUpdateNote(leadId, e.target.value);
  }

  formatLastActivity = lead => (
    lead.lastActivityType && `${getTaskTypeLabel(lead.lastActivityType)} on ${formatDateTime(lead.lastActivityDate)}`
  )

  renderNotification(leadId) {
    const className = classNames('pull-right', 'note-save-notification', 'text-success', {
      visible: this.props.savedNoteForLeadId === leadId
    });

    return (
      <strong className={className} name="saved-notification">
        <FontAwesomeIcon className="mr-1" icon={faCheck} />
        Saved
      </strong>
    );
  }

  renderNotes = (lead) => {
    if (this.props.editable) {
      return (
        <td className="pb-1" name="notesInput">
          <textarea
            className="lead-notes"
            data-lead-id={lead.id}
            name="leadNoteToClient"
            placeholder="Add Lead comments to your client here (optional)..."
            rows={3}
            style={{ minWidth: '100%' }}
            value={lead.noteToClient.body}
            onChange={e => this.updateNote(lead.id, e)}
          />
          {this.renderNotification(lead.id)}
        </td>
      );
    }

    return <td>{lead.noteToClient.body}</td>;
  }

  renderLeadRow = (lead) => {
    let labels = [lead.referralSourceLabel, lead.statusLabel];
    if (this.showListingData()) labels.push(lead.listingLabel);
    labels = labels.filter(l => !!l);

    return (
      <SelectableRow hideCheckbox key={lead.id} name="lead-row">
        <td>
          {lead.displayName}
          {labels.map((label, i) => <p className="mb-0 text-muted visible-xs" key={i}>{label}</p>)}
        </td>
        <td className="hidden-xs">{lead.contactCompany}</td>
        <td className="hidden-xs">{lead.referralSourceLabel}</td>
        {this.showListingData() && <td className="hidden-xs">{lead.listingLabel}</td>}
        <td className="hidden-xs">{lead.statusLabel}</td>
        {!this.props.hideLastActivity && <td className="hidden-xs">{this.formatLastActivity(lead)}</td>}
        <td className="hidden-xs">{formatDateTime(lead.createdAt)}</td>
        {this.showNotesColumn() && this.renderNotes(lead)}
      </SelectableRow>
    );
  }

  renderFilterTopbar() {
    const leadStatuses = this.leadStatuses();
    const listings = this.listings();
    const leadCount = this.filteredLeads().length;
    const pageData = {
      currentPage: 1,
      itemCount: leadCount,
      itemSingularLabel: 'lead',
      pageSize: leadCount,
      totalCount: leadCount
    };

    return (
      <FilterTopbar className="panel-heading p-0">
        <FilterRow
          name="filters"
          pageData={pageData}
          searchPlaceholder="Search by name"
          searchText={this.state.searchText}
          onSearchChange={this.changeSearchText}
        >
          {this.showListingData() && (
            <div className="left-buffer-md">
              <SelectFilter
                defaultValue={listings[0]}
                getOptionValue={option => option.id}
                name="listing-filter"
                options={listings}
                value={this.currentListing()}
                onChange={this.changeListingFilter}
              />
            </div>
          )}
          <div className="left-buffer-md">
            <SelectFilter
              defaultValue={leadStatuses[0]}
              getOptionValue={option => option.id}
              name="status-filter"
              options={leadStatuses}
              value={this.currentStatus()}
              onChange={this.changeStatusFilter}
            />
          </div>
        </FilterRow>
      </FilterTopbar>
    );
  }

  renderColumnHeaders() {
    return (
      <React.Fragment>
        <th>Name</th>
        <th>Company</th>
        <th>Referral Source</th>
        {this.showListingData() && <th>Spaces</th>}
        <th>Status</th>
        {!this.props.hideLastActivity && <th>Last Activity</th>}
        <th>Creation Date</th>
        {this.showNotesColumn() && <th>Notes</th>}
      </React.Fragment>
    );
  }

  render() {
    return (
      <div className="table-container">
        <SearchPage>
          {this.renderFilterTopbar()}
          <SelectableRecordTable
            className="mt-4 bordered"
            columnHeaders={this.renderColumnHeaders()}
            hasSelectAll={false}
            hasSelectColumn={false}
          >
            {this.filteredLeads().map(this.renderLeadRow)}
          </SelectableRecordTable>
        </SearchPage>
      </div>
    );
  }
}
